import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Calendar, Users, AlertCircle, Loader2, Clock, CheckCircle } from 'lucide-react';
import { formatAtlanticDate, formatAtlanticTime } from '@/utils/time.utils.ts';
import { formatDateTime, getStatusColor, getStatusLabel } from '@/utils/event-attendee-status.utils.ts';
import { MyEventsService } from '@/api/myEventsService';
import { useCheckInAttendee } from '@/hooks/useCheckInAttendee';
import { CheckInConfirmDialog } from '@/components/CheckInConfirmDialog';
import { CancelRegistrationModal } from '@/components/CancelRegistrationModal';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useRefreshContext } from '@/contexts/RefreshContext';

export const UpcomingEventsPage = () => {
  const { t, i18n } = useTranslation('common');
  const { registerRefresh, unregisterRefresh } = useRefreshContext();
  const queryClient = useQueryClient();

  const {
    data,
    isLoading: loading,
    error,
  } = useQuery({
    queryKey: ['upcomingEvents'],
    queryFn: async () => {
      // Explicitly request all relevant statuses
      return await MyEventsService.getUpcomingEvents({
        statuses: ['registered', 'confirmed', 'waitlisted'],
      });
    },
    refetchOnMount: 'always',
    staleTime: 0,
  });

  const events = data?.events || [];

  // Register refresh function that invalidates cache and refetches
  useEffect(() => {
    registerRefresh('/upcoming', async () => {
      await queryClient.invalidateQueries({ queryKey: ['upcomingEvents'] });
    });
    return () => unregisterRefresh('/upcoming');
  }, [registerRefresh, unregisterRefresh, queryClient]);

  const checkInMutation = useCheckInAttendee();
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    eventId: string;
    attendeeId: string;
    attendeeName: string;
    eventName: string;
  } | null>(null);
  const [checkInError, setCheckInError] = useState<string | null>(null);

  const [cancelModal, setCancelModal] = useState<{
    isOpen: boolean;
    eventId: string;
    eventName: string;
  } | null>(null);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const cancelMutation = useMutation({
    mutationFn: async ({ eventId, reason }: { eventId: string; reason: string }) => {
      return await MyEventsService.cancelRegistration(eventId, reason);
    },
    onSuccess: (data) => {
      setCancelModal(null);
      setCancelError(null);
      toast.success(t('upcomingEvents.cancelSuccess', { count: data.cancelledCount }));
      queryClient.invalidateQueries({ queryKey: ['upcomingEvents'] });
    },
    onError: (error: unknown) => {
      let errorMessage = t('upcomingEvents.cancelError');

      const err = error as {
        response?: { data?: { error?: { message?: string }; message?: string } };
        message?: string;
      };
      if (err?.response?.data?.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }

      setCancelError(errorMessage);
      // The modal stays open so the user can see the error message and their entered reason is preserved
    },
  });

  const handleCheckInClick = (eventId: string, attendeeId: string, attendeeName: string, eventName: string) => {
    setConfirmDialog({
      isOpen: true,
      eventId,
      attendeeId,
      attendeeName,
      eventName,
    });
  };

  const handleConfirmCheckIn = () => {
    if (!confirmDialog) return;

    // Clear previous error
    setCheckInError(null);

    checkInMutation.mutate(
      {
        eventId: confirmDialog.eventId,
        attendeeId: confirmDialog.attendeeId,
        attendeeName: confirmDialog.attendeeName,
      },
      {
        onSuccess: () => {
          setConfirmDialog(null);
          setCheckInError(null);
          // Reload events to show updated status
          queryClient.invalidateQueries({ queryKey: ['upcomingEvents'] });
        },
        onError: (error: unknown) => {
          // Extract error message for display in dialog
          let errorMessage = 'Failed to check in. Please try again.';

          const err = error as {
            response?: { data?: { error?: { message?: string }; message?: string } };
            message?: string;
          };
          if (err?.response?.data?.error?.message) {
            errorMessage = err.response.data.error.message;
          } else if (err?.response?.data?.message) {
            errorMessage = err.response.data.message;
          } else if (err?.message) {
            errorMessage = err.message;
          }

          setCheckInError(errorMessage);
          // Error toast is also shown by the hook's onError handler
        },
      },
    );
  };

  const handleCancelCheckIn = () => {
    setConfirmDialog(null);
    setCheckInError(null);
  };

  const handleCancelRegistrationClick = (eventId: string, eventName: string) => {
    setCancelModal({
      isOpen: true,
      eventId,
      eventName,
    });
  };

  const handleConfirmCancelRegistration = (reason: string) => {
    if (!cancelModal) return;
    setCancelError(null);
    cancelMutation.mutate({
      eventId: cancelModal.eventId,
      reason,
    });
  };

  const handleCloseCancelRegistration = () => {
    setCancelModal(null);
    setCancelError(null);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <div>
              <CardTitle className="flex items-center gap-2 text-[var(--color-neutral-900)]">
                <Calendar className="h-6 w-6" />
                {t('upcomingEvents.title')}
              </CardTitle>
              <CardDescription className="text-[var(--color-neutral-700)]">
                {t('upcomingEvents.description')}
              </CardDescription>
            </div>
          </CardHeader>
        </Card>

        {error && (
          <Card className="border-[var(--color-warning-500)]/30 bg-[var(--color-warning-100)]">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-[var(--color-warning-700)]">
                <AlertCircle className="h-5 w-5" />
                <p className="text-sm">{(error as Error)?.message || 'Failed to load events'}</p>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5" />
            <h2 className="text-xl font-semibold text-[var(--color-neutral-900)]">
              {t('upcomingEvents.registeredEvents')} ({loading ? '...' : events.length})
            </h2>
          </div>

          {loading ? (
            <Card>
              <CardContent className="text-center py-12">
                <Loader2 className="mx-auto h-12 w-12 text-[var(--color-neutral-400)] mb-4 animate-spin" />
                <h3 className="text-lg font-medium mb-2 text-[var(--color-neutral-900)]">
                  {t('upcomingEvents.loading')}
                </h3>
                <p className="text-sm text-[var(--color-neutral-700)]">{t('upcomingEvents.loadingDescription')}</p>
              </CardContent>
            </Card>
          ) : events.length > 0 ? (
            <div className="space-y-4">
              {events.map((event) => (
                <Card key={event.eventId} className="transition-shadow hover:shadow-md">
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-lg text-[var(--color-neutral-900)]">{event.eventName}</CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1 text-[var(--color-neutral-700)]">
                          <Calendar className="h-4 w-4 ml-4 text-[var(--color-primary-700)]" />
                          <span title="Start event">
                            {formatAtlanticDate(event.eventDate, i18n.language)} {t('events.at')}{' '}
                            {formatAtlanticTime(event.eventDate, i18n.language)}
                          </span>
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="space-y-3">
                      <h4 className="text-sm font-medium text-(--color-neutral-900)">
                        {t('upcomingEvents.familyMembersRegistered')}
                      </h4>
                      <div className="grid grid-cols-1 gap-2">
                        {event.attendees.map((attendee) => (
                          <div
                            key={attendee.attendeeId}
                            className="flex items-center justify-between bg-[var(--color-neutral-300)]/20 rounded-lg p-3 hover:bg-[var(--color-neutral-300)]/30 transition-colors"
                          >
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <div
                                  className={`h-2 w-2 rounded-full ${
                                    attendee.registrationStatus === 'confirmed'
                                      ? 'bg-[var(--color-success-500)]'
                                      : 'bg-[var(--color-secondary-500)]'
                                  }`}
                                ></div>
                                <span className="text-sm font-medium text-[var(--color-neutral-900)]">
                                  {attendee.attendeeName || 'Unknown'}
                                </span>
                              </div>
                              {attendee.confirmedAt && (
                                <div className="flex items-center gap-2 ml-2">
                                  <Clock className="h-4 w-4 text-[var(--color-neutral-700)]" />
                                  <span className="text-xs text-[var(--color-neutral-700)]" title="confirmed">
                                    {formatDateTime(attendee.confirmedAt)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {attendee.registrationStatus === 'confirmed' ? (
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() =>
                                    handleCheckInClick(
                                      event.eventId,
                                      attendee.attendeeId,
                                      attendee.attendeeName || 'Unknown',
                                      event.eventName,
                                    )
                                  }
                                  disabled={
                                    checkInMutation.isPending && confirmDialog?.attendeeId === attendee.attendeeId
                                  }
                                  className="flex items-center gap-1 min-h-6 px-2 text-xs"
                                >
                                  {checkInMutation.isPending && confirmDialog?.attendeeId === attendee.attendeeId ? (
                                    <>
                                      <Loader2 className="h-3 w-3 animate-spin" />
                                      {t('upcomingEvents.checkingIn')}
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="h-3 w-3" />
                                      {t('upcomingEvents.checkIn')}
                                    </>
                                  )}
                                </Button>
                              ) : (
                                <Badge
                                  variant={null}
                                  className={`whitespace-nowrap flex-shrink-0 ${getStatusColor(attendee.registrationStatus)}`}
                                >
                                  {t(getStatusLabel(attendee.registrationStatus))}
                                </Badge>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-3 border-t border-[var(--color-neutral-200)] flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleCancelRegistrationClick(event.eventId, event.eventName)}
                      className="w-full sm:w-auto text-[var(--color-error-700)] border-[var(--color-error-300)] hover:bg-[var(--color-error-50)]"
                    >
                      {t('upcomingEvents.cancelRegistration')}
                    </Button>
                  </CardFooter>
                </Card>
              ))}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-12">
                <Calendar className="mx-auto h-12 w-12 text-[var(--color-neutral-400)] mb-4" />
                <h3 className="text-lg font-medium mb-2 text-[var(--color-neutral-900)]">
                  {t('upcomingEvents.noEvents')}
                </h3>
                <p className="text-sm text-[var(--color-neutral-700)] mb-6">
                  {t('upcomingEvents.noEventsDescription')}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Check-in Confirmation Dialog */}
      {confirmDialog && (
        <CheckInConfirmDialog
          isOpen={confirmDialog.isOpen}
          attendeeName={confirmDialog.attendeeName}
          eventName={confirmDialog.eventName}
          onConfirm={handleConfirmCheckIn}
          onCancel={handleCancelCheckIn}
          isCheckingIn={checkInMutation.isPending}
          error={checkInError}
        />
      )}

      {/* Cancel Registration Modal */}
      {cancelModal && (
        <CancelRegistrationModal
          isOpen={cancelModal.isOpen}
          eventName={cancelModal.eventName}
          onConfirm={handleConfirmCancelRegistration}
          onCancel={handleCloseCancelRegistration}
          isCancelling={cancelMutation.isPending}
          error={cancelError}
        />
      )}
    </Layout>
  );
};
